import { Divider, Stack } from '@mui/material';
import { Button, colors, Typography } from '@sweep-io/sweep-design';
import SweepTextField from '../../../common/SweepTextField';
import { memo, useState } from 'react';
import { usePrompt } from '../usePrompt';
import { Prompt } from '@server/prompts';
import { AgentChatInstanceProvider } from '../../AgentChatInstanceContext';
import { ChatInstance } from '../../ChatInstance';
import { AgentChat } from '../../AgentChat';
import useAgentsChats from '../../useAgentsChats';
import { useRunOnceWhenTruthy } from '../../../common/useRunOnceWhenTruthy';

interface SinglePromptEditModeProps {
  promptInEdit: Prompt;
  setPromptInEdit: (prompt: Prompt) => void;
}

const SinglePromptEditMode = memo(
  ({ promptInEdit, setPromptInEdit }: SinglePromptEditModeProps) => {
    const [isOptimizing, setIsOptimizing] = useState(false);
    const { optimizePrompt } = usePrompt();
    const { activeAgent, activeChat, onConfirmMessage, createGhostChat } = useAgentsChats();
    const isChatActive = activeChat?.data?.aiChatDetails?.messages?.length ?? 0 > 0;

    useRunOnceWhenTruthy(() => {
      if (!activeAgent) return;
      createGhostChat({ agentId: activeAgent?.id, agentType: activeAgent?.type });
    }, !!activeAgent);

    return (
      <Stack direction="row" gap={3} pt={2} width="100%" height="100%" sx={{ minHeight: 0 }}>
        <Stack pb={4} flex={1} height="100%" width="100%" maxWidth={'50%'}>
          <Stack
            sx={{
              flex: 1,
              minHeight: 0,
              justifyContent: 'space-between',
              position: 'relative',
              backgroundColor: colors.grey[100],
              p: 2,
              borderRadius: '8px',
            }}
          >
            <Stack sx={{ flexShrink: 0, mb: 3 }}>
              <SweepTextField
                label="Name"
                value={promptInEdit?.name}
                onChange={(e) => setPromptInEdit({ ...promptInEdit, name: e?.target.value ?? '' })}
                disableConfirmPropagation
                TextFieldProps={{
                  placeholder: 'e.g. Summarize bundle options and attributes',
                  multiline: true,
                  rows: 2,
                }}
                autoFocus
              />
            </Stack>

            <Stack
              sx={{ flex: 1, minHeight: 0, justifyContent: 'space-between', position: 'relative' }}
            >
              <Stack position="absolute" top={1} right={2} zIndex={1}>
                <Button
                  variant="flat-blue"
                  size="large"
                  startIconName="EditAI"
                  disabled={!promptInEdit?.promptText}
                  loading={isOptimizing}
                  onClick={async () => {
                    setIsOptimizing(true);
                    await optimizePrompt(promptInEdit?.promptText ?? '').then((optimizedPrompt) => {
                      setIsOptimizing(false);
                      if (optimizedPrompt) {
                        setPromptInEdit({ ...promptInEdit, promptText: optimizedPrompt });
                      }
                    });
                  }}
                >
                  Optimize
                </Button>
              </Stack>

              <Stack sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
                <SweepTextField
                  label="Prompt"
                  value={promptInEdit?.promptText}
                  FormControlProps={{
                    sx: {
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    },
                  }}
                  TextFieldProps={{
                    multiline: true,
                    placeholder:
                      'e.g. For a specific Bundle, list its Options and Configuration Attributes. Useful for scoping complexity ahead of migration.',
                    sx: {
                      height: '100%',
                      '& .MuiInputBase-root': {
                        height: '100%',
                        alignItems: 'stretch',
                      },
                      '& textarea': {
                        height: '100% !important',
                        resize: 'none',
                        overflow: 'auto !important',
                        minHeight: '0 !important',
                      },
                    },
                  }}
                  onChange={(e) =>
                    setPromptInEdit({ ...promptInEdit, promptText: e?.target.value ?? '' })
                  }
                  disableConfirmPropagation
                  autoFocus={false}
                />
                <Stack position="absolute" bottom="16px" right="20px" zIndex={1}>
                  <Button
                    size="small"
                    onClick={() =>
                      activeAgent &&
                      onConfirmMessage({
                        messageStr: promptInEdit?.promptText ?? '',
                        agentId: activeAgent.id,
                        agentType: activeAgent.type,
                        isFirstMessage: true,
                        disableNavigation: true,
                      })
                    }
                  >
                    Test prompt
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Divider orientation="vertical" flexItem />
        <Stack
          flex={1}
          height="100%"
          maxWidth={'50%'}
          width="100%"
          sx={{ display: 'flex', flexDirection: 'column', pb: 4 }}
        >
          <Stack sx={{ flexShrink: 0 }}>
            <Typography variant="h3">Conversation preview</Typography>
          </Stack>
          {!isChatActive && (
            <Stack pt={2}>
              <Typography variant="body" color={colors.grey[700]}>
                Your prompt response will be shown here
              </Typography>
            </Stack>
          )}
          {!!isChatActive && (
            <Stack
              flex={1}
              sx={{
                minHeight: 0,
                '& .agent-chat-input-wrapper': { p: 0, pl: 1 },
                ml: '-8px',
              }}
            >
              <AgentChat
                noteAlignment="left"
                hideFirstMessage
                hideOverlayTop
                hideAgentNameButton
                scrollAreaPadding={0}
                hideAgentInfo
              />
            </Stack>
          )}
        </Stack>
      </Stack>
    );
  },
);

const SinglePromptEditModeWithInstanceContext = (props: SinglePromptEditModeProps) => {
  return (
    <AgentChatInstanceProvider instanceId={ChatInstance.editPrompt}>
      <SinglePromptEditMode {...props} />
    </AgentChatInstanceProvider>
  );
};

export { SinglePromptEditModeWithInstanceContext as SinglePromptEditMode };
